import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Course, UserSession, Question, CourseProgress, resolveAssetUrl } from '../types';
import { api, progressStore } from '../services/api';
import { useLanguage } from '../context/LanguageContext';
import { BranchingScenarioGame } from '../components/BranchingScenarioGame';
import { SkillTreeMap } from '../components/SkillTreeMap';
import { SocialImpactBanner } from '../components/SocialImpactBanner';
import {
  ArrowLeft,
  BookOpen,
  CheckCircle,
  Clock,
  FileCheck,
  Gamepad2,
  HelpCircle,
  Play,
  Shield,
  Sparkles,
  Award,
  AlertTriangle,
  ChevronRight,
  ChevronLeft,
  Flame,
  Check,
  X,
  RotateCcw,
  Target,
  Save,
  CheckCircle2,
  Video,
  Code,
  Copy,
  Terminal
} from 'lucide-react';

interface CourseLearningPageProps {
  session: UserSession;
}

type ActiveView =
  | { type: 'hook' }
  | { type: 'module'; moduleIndex: number; pageIndex: number; tab: 'lesson' | 'check' }
  | { type: 'minigame' }
  | { type: 'exam' };

// High-impact psychological curiosity hooks
const COURSE_HOOKS: Record<string, { stat: string; hook: { es: string; en: string }; takeaway: { es: string; en: string } }> = {
  'COURSE-SG-SST-01': {
    stat: 'El 84% de las fatalidades laborales en LatAm pudieron evitarse con la aplicación del ciclo PHVA y controles de ingeniería.',
    hook: {
      es: '¿Sabías que una caída a más de 2 metros sin permiso de trabajo formal no solo expone a sanciones de hasta 500 SMLMV, sino que compromete la responsabilidad penal del empleador?',
      en: 'Did you know that working above 2m without a permit exposes companies to severe legal fines and personal criminal liability for management?'
    },
    takeaway: {
      es: 'En este curso aprenderás a liderar la prevención con rigor técnico, blindando legal y operativamente a tu equipo.',
      en: 'In this course you will master technical hazard prevention, protecting your team and legal standing.'
    }
  },
  'COURSE-GDPR-01': {
    stat: 'Las multas por infracciones al RGPD superaron los 2.100 millones de euros en el último año debido a consentimientos tácitos inválidos.',
    hook: {
      es: '¿Tu organización aún usa casillas pre-marcadas o almacena datos sin un Registro de Actividades de Tratamiento (RAT)? Estás a una auditoría de una brecha crítica.',
      en: 'Is your company still using pre-ticked boxes? You might be one regulatory audit away from severe compliance penalties.'
    },
    takeaway: {
      es: 'Domina los derechos ARCO, la gestión de brechas en 72h y la privacidad desde el diseño para certificar tu gobernanza de datos.',
      en: 'Master data subject rights, 72-hour breach alerts, and privacy-by-design to certify compliance.'
    }
  },
  'COURSE-SARLAFT-01': {
    stat: 'Más de USD $2 billones se lavan globalmente cada año. Las empresas reales son el blanco principal para crear estructuras de fachada.',
    hook: {
      es: 'Una sola transacción no reportada a tiempo a la UIAF o una debida diligencia superficial a una PEP puede acarrear extinción de dominio e inclusión en Lista Clinton (OFAC).',
      en: 'A single unreported suspicious transaction or superficial PEP check can trigger asset forfeiture and OFAC sanction listing.'
    },
    takeaway: {
      es: 'Aprenderás a detectar señales de alerta sutiles, aplicar Debida Diligencia Intensificada y radicar reportes ROS con absoluta reserva legal.',
      en: 'Learn to detect subtle red flags, apply Enhanced Due Diligence, and file confidential STR reports.'
    }
  },
  'COURSE-CYBERSEC-01': {
    stat: 'El 91% de los ciberataques exitosos inician con un correo de phishing dirigido que suplanta una notificación corporativa.',
    hook: {
      es: 'En menos de 4 minutos, un solo clic en un enlace malicioso puede descargar un ransomware que paralice toda la infraestructura en la nube.',
      en: 'In under 4 minutes, a single malicious link click can deploy ransomware capable of crippling cloud infrastructure.'
    },
    takeaway: {
      es: 'Aplica el modelo Zero Trust, reconoce vectores de ingeniería social avanzados y activa los protocolos de aislamiento del SOC.',
      en: 'Deploy Zero Trust thinking, spot advanced social engineering, and trigger rapid SOC containment.'
    }
  },
  'COURSE-CODEOFCONDUCT-01': {
    stat: 'Las empresas con comités de convivencia activos reducen en un 73% el ausentismo y previenen litigios laborales de alto costo.',
    hook: {
      es: 'Bajo la Ley 1010, tolerar conductas de persecución o desprotección laboral acarrea fueros de estabilidad reforzada de 6 meses y sanciones directas.',
      en: 'Under anti-harassment statutes, workplace harassment yields 6-month anti-retaliation protections and direct corporate liabilities.'
    },
    takeaway: {
      es: 'Conoce los canales seguros de denuncia, la mediación preventiva del Comité de Convivencia y los límites éticos inquebrantables de la empresa.',
      en: 'Master confidential reporting, conciliatory committee mediation, and unwavering corporate ethical standards.'
    }
  }
};

// Nano Banana / Synthesia AI Video Generation Prompts & API Commands
const NANO_BANANA_PROMPTS: Record<string, Record<string, { voiceover_es: string; voiceover_en: string; visual_prompt: string; api_command: string }>> = {
  'COURSE-SG-SST-01': {
    'mod-sgsst-1': {
      voiceover_es: 'Bienvenidos al Sistema de Gestión de Seguridad y Salud en el Trabajo bajo el Decreto 1072. En este módulo analizaremos el ciclo PHVA y la matriz de peligros IPEVR para identificar riesgos antes de que se conviertan en incidentes.',
      voiceover_en: 'Welcome to the Occupational Safety and Health Management System under Decreto 1072. In this module, we explore the PDCA cycle and hazard matrix to mitigate risks proactively.',
      visual_prompt: 'High-end 4K hyper-realistic corporate industrial plant, professional safety officer in clean high-vis vest and smart helmet pointing at holographic holographic hazard heatmap overlay, cinematic lighting, 8k.',
      api_command: 'banana video generate --model "synthesia-v3-corporate" --avatar "alex_compliance_latam" --duration 90 --prompt "SG-SST Decreto 1072 Module 1"'
    },
    'mod-sgsst-2': {
      voiceover_es: 'Las tareas de alto riesgo como alturas y espacios confinados requieren permisos formales y arnés certificado. El COPASST es el órgano paritario que supervisa estas medidas.',
      voiceover_en: 'High-risk tasks such as working at heights require certified permits and PPE. COPASST oversees workplace health measures.',
      visual_prompt: 'Cinematic warehouse shot, certified technician using fall arrest lifeline and digital tablet inspection checklist, clean minimalist aesthetic.',
      api_command: 'banana video generate --model "synthesia-v3-corporate" --avatar "alex_compliance_latam" --duration 90 --prompt "SG-SST High Risk Controls Module 2"'
    },
    'mod-sgsst-3': {
      voiceover_es: 'En emergencias, cada segundo cuenta. La brigada integral y la investigación de incidentes permiten corregir causas raíz y cerrar brechas legales.',
      voiceover_en: 'In emergency response, seconds count. Incident investigation and root cause analysis prevent future hazards.',
      visual_prompt: 'Emergency command room with real-time evacuation telemetry dashboards, safety leadership team reviewing root-cause diagrams.',
      api_command: 'banana video generate --model "synthesia-v3-corporate" --avatar "alex_compliance_latam" --duration 90 --prompt "SG-SST Emergency & Investigation Module 3"'
    }
  },
  'COURSE-GDPR-01': {
    'mod-gdpr-1': {
      voiceover_es: 'El Reglamento General de Protección de Datos establece el principio de licitud y transparencia. Ningún dato sensible debe recopilarse sin consentimiento explícito e informado.',
      voiceover_en: 'The General Data Protection Regulation mandates lawfulness and transparency. No sensitive data may be gathered without clear consent.',
      visual_prompt: 'Modern European tech headquarters, data privacy officer reviewing encrypted server logs and consent management workflows on transparent glass OLED screen.',
      api_command: 'banana video generate --model "synthesia-v3-corporate" --avatar "elena_dpo_eu" --duration 90 --prompt "GDPR Foundations Module 1"'
    }
  }
};

export const CourseLearningPage: React.FC<CourseLearningPageProps> = ({ session }) => {
  const { courseId } = useParams<{ courseId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { lang, t } = useLanguage();

  const [course, setCourse] = useState<Course | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [activeView, setActiveView] = useState<ActiveView>({ type: 'hook' });

  // Video Prompt Inspector Drawer
  const [showVideoPromptModal, setShowVideoPromptModal] = useState<boolean>(false);
  const [copiedPrompt, setCopiedPrompt] = useState<boolean>(false);

  // Real-time course progress & persistence state
  const [courseProgress, setCourseProgress] = useState<CourseProgress | null>(null);
  const [badgeRecord, setBadgeRecord] = useState<any>(null);
  const [lastSavedTime, setLastSavedTime] = useState<string>('');

  // In-Module Quick Check state
  const [inModuleAnswers, setInModuleAnswers] = useState<Record<string, number>>({});
  const [inModuleSubmitted, setInModuleSubmitted] = useState<Record<string, boolean>>({});

  // Decision Mini-Game state
  const [selectedGameOption, setSelectedGameOption] = useState<string | null>(null);
  const [gameResult, setGameResult] = useState<any>(null);

  // 12-Question Randomized Assessment state
  const [randomSeed, setRandomSeed] = useState<number>(() => Date.now());
  const [selectedAnswers, setSelectedAnswers] = useState<Record<string, number>>({});
  const [currentQuizIndex, setCurrentQuizIndex] = useState<number>(0);
  const [isSubmittingQuiz, setIsSubmittingQuiz] = useState<boolean>(false);
  const [quizResult, setQuizResult] = useState<any>(null);
  const [showPendingConfirm, setShowPendingConfirm] = useState<boolean>(false);

  // Load course and progress
  useEffect(() => {
    if (!courseId) return;
    setLoading(true);
    api.getCourseById(courseId).then((data) => {
      if (data) {
        setCourse(data);

        // Retrieve persisted progress
        const cp = progressStore.getCourseProgress(session.userId, courseId);
        setCourseProgress(cp);
        setLastSavedTime(new Date().toLocaleTimeString());

        const userBadges = progressStore.getBadges(session.userId);
        const existingBadge = userBadges.find((b) => b.courseId === courseId);
        if (existingBadge) {
          setBadgeRecord(existingBadge);
        }

        // Deep link tab navigation or resume previous position
        const tabParam = searchParams.get('tab');
        if (tabParam === 'exam') {
          setActiveView({ type: 'exam' });
        } else if (tabParam === 'game') {
          setActiveView({ type: 'minigame' });
        } else if (tabParam === 'module') {
          const modIdx = cp.lastVisitedModuleIndex || 0;
          const pageIdx = cp.lastVisitedPageIndex || 0;
          setActiveView({ type: 'module', moduleIndex: modIdx, pageIndex: pageIdx, tab: 'lesson' });
        } else if (cp.status === 'IN_PROGRESS' && cp.lastVisitedModuleIndex !== undefined) {
          // Auto resume at last visited position
          setActiveView({
            type: 'module',
            moduleIndex: cp.lastVisitedModuleIndex,
            pageIndex: cp.lastVisitedPageIndex || 0,
            tab: 'lesson'
          });
        }
      }
      setLoading(false);
    });
  }, [courseId, session.userId, searchParams]);

  // Sample exactly 12 questions using seeded random
  const sampledQuestions: Question[] = useMemo(() => {
    if (!course || !course.questionBank) return [];
    const bank = [...course.questionBank];
    let seed = randomSeed;
    const rng = () => {
      const x = Math.sin(seed++) * 10000;
      return x - Math.floor(x);
    };
    for (let i = bank.length - 1; i > 0; i--) {
      const j = Math.floor(rng() * (i + 1));
      [bank[i], bank[j]] = [bank[j], bank[i]];
    }
    return bank.slice(0, 12);
  }, [course, randomSeed]);

  // Handler for changing lesson / module with automatic position & page progress save
  const handleNavigateLesson = (moduleIndex: number, pageIndex: number, tab: 'lesson' | 'check' = 'lesson') => {
    if (!courseId) return;
    setActiveView({ type: 'module', moduleIndex, pageIndex, tab });
    progressStore.updatePosition(session.userId, courseId, moduleIndex, pageIndex);
    const mod = course?.modules[moduleIndex];
    if (mod && mod.pages?.[pageIndex]) {
      const updatedCp = progressStore.markPageComplete(session.userId, courseId, mod.moduleId, mod.pages[pageIndex].pageId);
      setCourseProgress(updatedCp);
      setLastSavedTime(new Date().toLocaleTimeString());
    }
  };

  const handleMarkModuleComplete = (moduleId: string) => {
    if (!courseId) return;
    const updatedCp = progressStore.markModuleComplete(session.userId, courseId, moduleId);
    setCourseProgress(updatedCp);
    setLastSavedTime(new Date().toLocaleTimeString());
  };

  const handleCompleteMiniGame = (option: any) => {
    if (!courseId) return;
    setGameResult(option);
    const updatedCp = progressStore.markMiniGameComplete(session.userId, courseId);
    setCourseProgress(updatedCp);
    setLastSavedTime(new Date().toLocaleTimeString());
  };

  const handleCompleteBranchingScenario = (unlockedReward?: any) => {
    if (!courseId) return;
    const updatedCp = progressStore.markBranchingScenarioComplete(session.userId, courseId, unlockedReward);
    setCourseProgress(updatedCp);
    setLastSavedTime(new Date().toLocaleTimeString());
  };

  const handleQuizAnswer = (qId: string, optionIdx: number) => {
    setSelectedAnswers((prev) => ({ ...prev, [qId]: optionIdx }));
  };

  const handleSubmitQuizFinal = async (allowPending: boolean = false) => {
    if (!course) return;

    const unansweredCount = sampledQuestions.filter((q) => selectedAnswers[q.qId] === undefined).length;
    if (unansweredCount > 0 && !allowPending) {
      setShowPendingConfirm(true);
      return;
    }

    setIsSubmittingQuiz(true);
    setShowPendingConfirm(false);

    const answersPayload = sampledQuestions.map((q) => ({
      qId: q.qId,
      selectedIndex: selectedAnswers[q.qId] !== undefined ? selectedAnswers[q.qId] : -1,
    }));

    try {
      const result = await api.submitAssessment(course.courseId, session.userId, answersPayload);
      setQuizResult(result);
      // Reload updated progress
      const updatedCp = progressStore.getCourseProgress(session.userId, course.courseId);
      setCourseProgress(updatedCp);
      setLastSavedTime(new Date().toLocaleTimeString());

      if (result.passed && result.badgeId) {
        const savedBadge = progressStore.getBadges(session.userId).find((b) => b.badgeId === result.badgeId);
        if (savedBadge) setBadgeRecord(savedBadge);
      }
    } catch (err: any) {
      alert(err.message || 'Error submitting assessment');
    } finally {
      setIsSubmittingQuiz(false);
    }
  };

  const handleRetakeQuiz = () => {
    setRandomSeed(Date.now());
    setSelectedAnswers({});
    setCurrentQuizIndex(0);
    setQuizResult(null);
    setShowPendingConfirm(false);
  };

  if (loading) {
    return (
      <div style={{ minHeight: '80vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div className="glass-card" style={{ padding: '32px', textAlign: 'center' }}>
          <Sparkles className="spin-slow" size={32} color="#3b82f6" />
          <p style={{ marginTop: '16px', color: 'var(--text-secondary)' }}>
            {t('Cargando entorno de aprendizaje y recuperando progreso guardado...', 'Loading learning environment & restoring saved progress...')}
          </p>
        </div>
      </div>
    );
  }

  if (!course) {
    return (
      <div style={{ padding: '40px 24px', maxWidth: '800px', margin: '0 auto', textAlign: 'center' }}>
        <h2>{t('Curso no encontrado', 'Course not found')}</h2>
        <button className="btn btn-primary" onClick={() => navigate('/')} style={{ marginTop: '16px' }}>
          <ArrowLeft size={16} /> {t('Volver al Catálogo', 'Back to Catalog')}
        </button>
      </div>
    );
  }

  const hookInfo = COURSE_HOOKS[course.courseId] || {
    stat: 'El 80% de los incidentes de cumplimiento se previenen con formación continua y toma de decisiones ética.',
    hook: {
      es: '¿Estás listo para dominar los estándares obligatorios de esta normativa y proteger a tu organización?',
      en: 'Are you ready to master the mandatory standards of this regulation?'
    },
    takeaway: {
      es: 'Aprende los conceptos clave, resuelve el mini-juego de decisiones y certifica tus competencias.',
      en: 'Learn core concepts, solve the decision mini-game, and certify your skills.'
    }
  };

  const progressPercent = courseProgress?.progressPercent ?? 0;
  const courseStatus = courseProgress?.status ?? 'NOT_STARTED';
  const completedModulesList = courseProgress?.moduleProgress?.filter((m) => m.completed).map((m) => m.moduleId) || [];
  const badgeSrc = resolveAssetUrl(course.badgeUrl, lang);

  // Active module AI video prompt if in module view
  const currentModId = activeView.type === 'module' ? course.modules[activeView.moduleIndex]?.moduleId : 'mod-sgsst-1';
  const currentVideoPrompt = (NANO_BANANA_PROMPTS[course.courseId]?.[currentModId]) || {
    voiceover_es: `Guión de video didáctico de 90 segundos para ${course.title.es}. Introducción de conceptos clave y marco regulatorio obligatorio.`,
    voiceover_en: `90-second compliance instructional video script for ${course.title.en}. Introduction to core regulatory standards and best practices.`,
    visual_prompt: `High-definition 4K corporate compliance instructional scene, professional compliance officer in modern tech workplace, crisp cinematic studio lighting, minimalist glassmorphism interface overlay.`,
    api_command: `banana video generate --model "synthesia-v3-corporate" --avatar "compliance_pro" --duration 90 --prompt "${course.courseId}"`
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: 'calc(100vh - 64px)' }}>
      {/* Top Breadcrumb & Status Bar with Persistent Progress Bar */}
      <div
        style={{
          background: 'var(--bg-secondary)',
          borderBottom: '1px solid var(--border-color)',
          padding: '12px 24px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '12px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <button
            onClick={() => navigate('/')}
            className="btn btn-secondary"
            style={{ padding: '6px 12px', fontSize: '12.5px', display: 'flex', alignItems: 'center', gap: '6px' }}
          >
            <ArrowLeft size={15} />
            {t('Catálogo de Cursos', 'Course Catalog')}
          </button>
          <span style={{ color: 'var(--text-muted)' }}>/</span>
          <span style={{ fontWeight: 700, fontSize: '14px', color: 'var(--text-primary)' }}>
            {course.title[lang]}
          </span>
          <span
            style={{
              background: 'rgba(59, 130, 246, 0.15)',
              color: '#60a5fa',
              fontSize: '11px',
              fontWeight: 600,
              padding: '2px 8px',
              borderRadius: '6px',
            }}
          >
            {course.jurisdiction}
          </span>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
          {/* Banana IA Video Prompt Modal Trigger */}
          <button
            className="btn btn-secondary"
            style={{ padding: '5px 10px', fontSize: '11.5px', display: 'flex', alignItems: 'center', gap: '5px' }}
            onClick={() => setShowVideoPromptModal(true)}
            title="Ver comandos de generación de video para Banana IA / Synthesia"
          >
            <Video size={13} color="#f59e0b" />
            <span>{t('Prompt Banana IA', 'Banana AI Video Prompt')}</span>
          </button>

          {/* Auto-Saved State Indicator */}
          {lastSavedTime && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '11.5px', color: '#10b981' }}>
              <Save size={13} />
              <span>{t('Guardado:', 'Saved:')} {lastSavedTime}</span>
            </div>
          )}

          {/* Interactive Progress Bar Widget */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
              <span style={{ fontSize: '12px', color: 'var(--text-primary)', fontWeight: 700 }}>
                {progressPercent}% {t('Completado', 'Completed')}
              </span>
              <span style={{ fontSize: '10.5px', color: 'var(--text-muted)' }}>
                {courseStatus === 'COMPLETED'
                  ? t('Completado', 'Completed')
                  : courseStatus === 'IN_PROGRESS'
                  ? t('En Progreso', 'In Progress')
                  : t('No Iniciado', 'Not Started')}
              </span>
            </div>
            <div
              style={{
                width: '120px',
                height: '8px',
                background: 'var(--bg-primary)',
                border: '1px solid var(--border-color)',
                borderRadius: '4px',
                overflow: 'hidden',
                position: 'relative',
              }}
            >
              <div
                style={{
                  width: `${progressPercent}%`,
                  height: '100%',
                  background: progressPercent === 100 ? 'linear-gradient(90deg, #10b981, #34d399)' : 'var(--gradient-brand)',
                  transition: 'width 0.4s ease',
                }}
              />
            </div>
          </div>

          {badgeRecord && (
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                padding: '4px 10px',
                borderRadius: '12px',
                background: 'rgba(16, 185, 129, 0.15)',
                color: '#34d399',
                border: '1px solid rgba(16, 185, 129, 0.3)',
                fontSize: '12px',
                fontWeight: 700,
              }}
            >
              <Award size={14} />
              {t('Insignia Certificada', 'Certified Badge')}
            </div>
          )}
        </div>
      </div>

      {/* Main 2-Column Workspace (Sidebar + Content View) */}
      <div style={{ display: 'flex', flex: 1, position: 'relative' }}>
        {/* Persistent AWS Skill Builder Left Sidebar */}
        <aside
          className="learning-sidebar"
          style={{
            width: '320px',
            background: 'var(--bg-secondary)',
            borderRight: '1px solid var(--border-color)',
            display: 'flex',
            flexDirection: 'column',
            flexShrink: 0,
            overflowY: 'auto',
          }}
        >
          {/* Course Badge Thumbnail Header with Sidebar Progress Bar */}
          <div style={{ padding: '20px', borderBottom: '1px solid var(--border-color)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
              {badgeSrc && (
                <img
                  src={badgeSrc}
                  alt="Badge"
                  style={{ width: '48px', height: '48px', objectFit: 'contain' }}
                />
              )}
              <div>
                <div style={{ fontSize: '11px', color: 'var(--accent-primary)', fontWeight: 700, textTransform: 'uppercase' }}>
                  {course.category}
                </div>
                <div style={{ fontSize: '13.5px', fontWeight: 700, color: 'var(--text-primary)', lineHeight: 1.3 }}>
                  {course.title[lang]}
                </div>
              </div>
            </div>

            {/* Sidebar Visual Progress Bar */}
            <div style={{ marginBottom: '8px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: 'var(--text-muted)', marginBottom: '4px' }}>
                <span>{t('Progreso del Curso', 'Course Progress')}</span>
                <strong style={{ color: progressPercent === 100 ? '#34d399' : 'var(--text-primary)' }}>{progressPercent}%</strong>
              </div>
              <div style={{ width: '100%', height: '6px', background: 'var(--bg-primary)', borderRadius: '3px', overflow: 'hidden' }}>
                <div style={{ width: `${progressPercent}%`, height: '100%', background: progressPercent === 100 ? '#10b981' : 'var(--accent-primary)', transition: 'width 0.3s ease' }} />
              </div>
            </div>

            <div style={{ fontSize: '11px', color: 'var(--text-secondary)', display: 'flex', justifyContent: 'space-between' }}>
              <span>{completedModulesList.length} / {course.modules.length} {t('módulos listos', 'modules done')}</span>
              <span>{t('Aprobación:', 'Pass:')} <strong>{course.passingScore}%</strong></span>
            </div>
          </div>

          {/* Navigation Structure */}
          <div style={{ padding: '12px 8px', display: 'flex', flexDirection: 'column', gap: '4px', flex: 1 }}>
            {/* 1. Curiosity Hook / Overview */}
            <button
              onClick={() => setActiveView({ type: 'hook' })}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                padding: '10px 14px',
                borderRadius: 'var(--radius-sm)',
                border: 'none',
                background: activeView.type === 'hook' ? 'rgba(59, 130, 246, 0.15)' : 'transparent',
                color: activeView.type === 'hook' ? '#60a5fa' : 'var(--text-primary)',
                fontWeight: activeView.type === 'hook' ? 700 : 500,
                fontSize: '13px',
                textAlign: 'left',
                cursor: 'pointer',
                transition: 'all 0.15s ease',
              }}
            >
              <Flame size={16} color={activeView.type === 'hook' ? '#f59e0b' : '#94a3b8'} />
              <span>{t('1. Disparador de Atención & Contexto', '1. Attention Hook & Context')}</span>
            </button>

            <div style={{ padding: '8px 14px 4px', fontSize: '10.5px', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              {t('Módulos de Aprendizaje', 'Learning Modules')}
            </div>

            {/* 2. Interactive Modules List */}
            {course.modules.map((mod, modIdx) => {
              const isComplete = completedModulesList.includes(mod.moduleId);
              const isActive = activeView.type === 'module' && activeView.moduleIndex === modIdx;

              return (
                <div key={mod.moduleId} style={{ marginBottom: '4px' }}>
                  <button
                    onClick={() => handleNavigateLesson(modIdx, 0, 'lesson')}
                    style={{
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '10px 14px',
                      borderRadius: 'var(--radius-sm)',
                      border: 'none',
                      background: isActive ? 'rgba(59, 130, 246, 0.15)' : 'transparent',
                      color: isActive ? '#60a5fa' : 'var(--text-primary)',
                      fontWeight: isActive ? 700 : 500,
                      fontSize: '12.5px',
                      textAlign: 'left',
                      cursor: 'pointer',
                      transition: 'all 0.15s ease',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', overflow: 'hidden' }}>
                      {isComplete ? (
                        <CheckCircle size={15} color="#10b981" style={{ flexShrink: 0 }} />
                      ) : (
                        <Play size={14} color="#94a3b8" style={{ flexShrink: 0 }} />
                      )}
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {mod.title[lang]}
                      </span>
                    </div>
                    <span style={{ fontSize: '10.5px', color: 'var(--text-muted)', flexShrink: 0 }}>
                      {mod.durationMinutes}m
                    </span>
                  </button>

                  {/* Sub-steps: Lesson Pages & In-Module Check */}
                  {isActive && activeView.type === 'module' && (
                    <div style={{ paddingLeft: '28px', paddingTop: '4px', display: 'flex', flexDirection: 'column', gap: '2px' }}>
                      {mod.pages?.map((p, pIdx) => {
                        const isPageDone = courseProgress?.completedPages?.[mod.moduleId]?.includes(p.pageId);

                        return (
                          <button
                            key={p.pageId}
                            onClick={() => handleNavigateLesson(modIdx, pIdx, 'lesson')}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              padding: '6px 10px',
                              borderRadius: '4px',
                              border: 'none',
                              background: activeView.tab === 'lesson' && activeView.pageIndex === pIdx ? 'var(--bg-primary)' : 'transparent',
                              color: activeView.tab === 'lesson' && activeView.pageIndex === pIdx ? 'var(--accent-primary)' : 'var(--text-secondary)',
                              fontSize: '11.5px',
                              textAlign: 'left',
                              cursor: 'pointer',
                            }}
                          >
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', overflow: 'hidden' }}>
                              <BookOpen size={12} />
                              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {t('Lección', 'Lesson')} {pIdx + 1}: {p.title[lang]}
                              </span>
                            </div>
                            {isPageDone && <CheckCircle2 size={11} color="#10b981" style={{ flexShrink: 0 }} />}
                          </button>
                        );
                      })}

                      {mod.moduleAssessment && (
                        <button
                          onClick={() => handleNavigateLesson(modIdx, 0, 'check')}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            padding: '6px 10px',
                            borderRadius: '4px',
                            border: 'none',
                            background: activeView.tab === 'check' ? 'rgba(245, 158, 11, 0.15)' : 'transparent',
                            color: activeView.tab === 'check' ? '#f59e0b' : 'var(--text-secondary)',
                            fontSize: '11.5px',
                            textAlign: 'left',
                            cursor: 'pointer',
                            fontWeight: 600,
                          }}
                        >
                          <HelpCircle size={12} />
                          <span>{t('Comprobación de Módulo', 'Module Knowledge Check')}</span>
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}

            <div style={{ padding: '8px 14px 4px', fontSize: '10.5px', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              {t('Gamificación & Evaluación', 'Gamification & Evaluation')}
            </div>

            {/* 3. Decision Mini-Game */}
            {course.miniGame && (
              <button
                onClick={() => setActiveView({ type: 'minigame' })}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '10px 14px',
                  borderRadius: 'var(--radius-sm)',
                  border: 'none',
                  background: activeView.type === 'minigame' ? 'rgba(16, 185, 129, 0.15)' : 'transparent',
                  color: activeView.type === 'minigame' ? '#34d399' : 'var(--text-primary)',
                  fontWeight: activeView.type === 'minigame' ? 700 : 500,
                  fontSize: '12.5px',
                  textAlign: 'left',
                  cursor: 'pointer',
                  transition: 'all 0.15s ease',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <Gamepad2 size={16} color={activeView.type === 'minigame' ? '#34d399' : '#94a3b8'} />
                  <span>{t('Mini-Juego de Decisiones', 'Decision Mini-Game')}</span>
                </div>
                {courseProgress?.miniGameCompleted && (
                  <CheckCircle size={14} color="#10b981" />
                )}
              </button>
            )}

            {/* 4. Final Assessment (12 Questions) */}
            <button
              onClick={() => setActiveView({ type: 'exam' })}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '10px 14px',
                borderRadius: 'var(--radius-sm)',
                border: 'none',
                background: activeView.type === 'exam' ? 'rgba(59, 130, 246, 0.2)' : 'transparent',
                color: activeView.type === 'exam' ? '#60a5fa' : 'var(--text-primary)',
                fontWeight: activeView.type === 'exam' ? 700 : 600,
                fontSize: '12.5px',
                textAlign: 'left',
                cursor: 'pointer',
                transition: 'all 0.15s ease',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <FileCheck size={16} color={activeView.type === 'exam' ? '#60a5fa' : '#94a3b8'} />
                <span>{t('Evaluación Final (12 Preguntas)', 'Final Assessment (12 Questions)')}</span>
              </div>
              {courseProgress?.examPassed && (
                <Award size={15} color="#10b981" />
              )}
            </button>
          </div>
        </aside>

        {/* Dynamic Center Main Learning Canvas */}
        <main style={{ flex: 1, padding: '32px', overflowY: 'auto', maxWidth: '1080px', margin: '0 auto' }}>
          {/* Psychology Hub: Social Proof Banner & Skill Tree Map */}
          <SocialImpactBanner companyName={(session as any).companyName || 'Akretrix Enterprise'} />
          {course && courseProgress && (
            <SkillTreeMap
              course={course}
              progress={courseProgress}
              activeTab={
                activeView.type === 'minigame'
                  ? 'game'
                  : activeView.type === 'exam'
                  ? 'exam'
                  : 'lessons'
              }
              onSelectTab={(tab) => {
                if (tab === 'game') setActiveView({ type: 'minigame' });
                else if (tab === 'exam') setActiveView({ type: 'exam' });
                else setActiveView({ type: 'module', moduleIndex: 0, pageIndex: 0, tab: 'lesson' });
              }}
            />
          )}

          {/* VIEW 1: CURIOSITY GAP & ATTENTION HOOK */}
          {activeView.type === 'hook' && (
            <div>
              {/* Hook Hero Banner */}
              <div
                style={{
                  padding: '36px',
                  borderRadius: 'var(--radius-lg)',
                  background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.15) 0%, rgba(30, 41, 59, 0.8) 100%)',
                  border: '1px solid rgba(239, 68, 68, 0.3)',
                  marginBottom: '28px',
                }}
              >
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '4px 12px', borderRadius: '12px', background: 'rgba(239, 68, 68, 0.25)', color: '#f87171', fontSize: '12px', fontWeight: 700, marginBottom: '14px' }}>
                  <Flame size={15} />
                  {t('Disparador de Consciencia & Riesgo Real', 'Real Risk & Attention Trigger')}
                </div>
                <h2 style={{ fontSize: '24px', fontWeight: 800, color: '#ffffff', lineHeight: 1.3, marginBottom: '16px' }}>
                  "{hookInfo.stat}"
                </h2>
                <p style={{ fontSize: '15px', color: '#e2e8f0', lineHeight: 1.6, marginBottom: '20px' }}>
                  {hookInfo.hook[lang]}
                </p>
                <div style={{ padding: '16px', borderRadius: '8px', background: 'rgba(15, 23, 42, 0.6)', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
                  <p style={{ fontSize: '13.5px', color: '#93c5fd', margin: 0 }}>
                    <strong>{t('Objetivo de Maestría:', 'Mastery Goal:')}</strong> {hookInfo.takeaway[lang]}
                  </p>
                </div>
              </div>

              {/* Course Architecture Overview Grid */}
              <h3 style={{ fontSize: '18px', fontWeight: 700, marginBottom: '16px' }}>
                {t('Ruta Pedagógica del Curso', 'Course Pedagogical Roadmap')}
              </h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '20px', marginBottom: '32px' }}>
                <div className="glass-card" style={{ padding: '20px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: 'var(--accent-primary)', fontWeight: 700, marginBottom: '8px' }}>
                    <BookOpen size={18} /> {t('Fase 1: Aprender', 'Phase 1: Learn')}
                  </div>
                  <p style={{ fontSize: '13px', color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                    {t(
                      '3 módulos estructurados con video didáctico e infografías vectoriales bilingües para desglosar la normativa en micro-lecciones digeribles.',
                      '3 structured modules with instructional video and bilingual vector diagrams breaking down standards into bite-sized lessons.'
                    )}
                  </p>
                </div>

                <div className="glass-card" style={{ padding: '20px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#34d399', fontWeight: 700, marginBottom: '8px' }}>
                    <Gamepad2 size={18} /> {t('Fase 2: Practicar', 'Phase 2: Practice')}
                  </div>
                  <p style={{ fontSize: '13px', color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                    {t(
                      'Simulador de decisiones situacionales. Enfréntate a un dilema de cumplimiento corporativo y evalúa las consecuencias sin riesgo real.',
                      'Situational decision simulator. Navigate a corporate dilemma and assess outcomes safely.'
                    )}
                  </p>
                </div>

                <div className="glass-card" style={{ padding: '20px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#f59e0b', fontWeight: 700, marginBottom: '8px' }}>
                    <Target size={18} /> {t('Fase 3: Validar (80%)', 'Phase 3: Validate (80%)')}
                  </div>
                  <p style={{ fontSize: '13px', color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                    {t(
                      'Evaluación oficial de 12 preguntas aleatorias. Al superar el 80% (10/12), se emite automáticamente la credencial Open Badges 3.0.',
                      'Official 12-question randomized assessment. Scoring 80%+ (10/12) triggers Open Badges 3.0 certification.'
                    )}
                  </p>
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <button
                  className="btn btn-primary"
                  style={{ padding: '12px 24px', fontSize: '14px' }}
                  onClick={() => handleNavigateLesson(0, 0, 'lesson')}
                >
                  {t('Comenzar Módulo 1', 'Start Module 1')} <ChevronRight size={16} />
                </button>
              </div>
            </div>
          )}

          {/* VIEW 2: DEDICATED MODULE LESSON & IN-MODULE QUICK CHECK */}
          {activeView.type === 'module' && (
            <div>
              {(() => {
                const mod = course.modules[activeView.moduleIndex];
                const currentPage = mod.pages?.[activeView.pageIndex];

                if (activeView.tab === 'check' && mod.moduleAssessment) {
                  const check = mod.moduleAssessment;
                  const selectedOpt = inModuleAnswers[check.qId];
                  const submitted = inModuleSubmitted[check.qId];

                  return (
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#f59e0b', fontSize: '12px', fontWeight: 700, marginBottom: '8px' }}>
                        <HelpCircle size={15} />
                        {t('Comprobación Rápida de Aprendizaje', 'Quick Knowledge Check')} - {mod.title[lang]}
                      </div>
                      <h2 style={{ fontSize: '20px', fontWeight: 800, marginBottom: '20px' }}>
                        {check.questionText[lang]}
                      </h2>

                      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '24px' }}>
                        {check.options[lang].map((optText, optIdx) => {
                          const isSelected = selectedOpt === optIdx;
                          const isCorrect = optIdx === check.correctIndex;
                          let bg = 'var(--bg-secondary)';
                          let border = '1px solid var(--border-color)';
                          if (submitted) {
                            if (isCorrect) {
                              bg = 'rgba(16, 185, 129, 0.18)';
                              border = '1.5px solid #10b981';
                            } else if (isSelected && !isCorrect) {
                              bg = 'rgba(239, 68, 68, 0.18)';
                              border = '1.5px solid #ef4444';
                            }
                          } else if (isSelected) {
                            bg = 'rgba(59, 130, 246, 0.18)';
                            border = '1.5px solid var(--accent-primary)';
                          }

                          return (
                            <button
                              key={optIdx}
                              disabled={submitted}
                              onClick={() => setInModuleAnswers((prev) => ({ ...prev, [check.qId]: optIdx }))}
                              style={{
                                padding: '16px',
                                borderRadius: 'var(--radius-md)',
                                background: bg,
                                border,
                                textAlign: 'left',
                                color: 'var(--text-primary)',
                                fontSize: '13.5px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '12px',
                                cursor: submitted ? 'default' : 'pointer',
                                transition: 'all 0.15s ease',
                              }}
                            >
                              <div
                                style={{
                                  width: '24px',
                                  height: '24px',
                                  borderRadius: '50%',
                                  background: isSelected ? 'var(--accent-primary)' : 'var(--bg-primary)',
                                  color: isSelected ? '#ffffff' : 'var(--text-secondary)',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontSize: '12px',
                                  fontWeight: 700,
                                  flexShrink: 0,
                                }}
                              >
                                {String.fromCharCode(65 + optIdx)}
                              </div>
                              <span style={{ flex: 1 }}>{optText}</span>
                              {submitted && isCorrect && <Check size={18} color="#10b981" />}
                              {submitted && isSelected && !isCorrect && <X size={18} color="#ef4444" />}
                            </button>
                          );
                        })}
                      </div>

                      {submitted && (
                        <div
                          style={{
                            padding: '16px',
                            borderRadius: 'var(--radius-md)',
                            background: selectedOpt === check.correctIndex ? 'rgba(16, 185, 129, 0.12)' : 'rgba(239, 68, 68, 0.12)',
                            border: `1px solid ${selectedOpt === check.correctIndex ? 'rgba(16, 185, 129, 0.3)' : 'rgba(239, 68, 68, 0.3)'}`,
                            marginBottom: '24px',
                          }}
                        >
                          <div style={{ fontWeight: 700, fontSize: '14px', marginBottom: '6px', color: selectedOpt === check.correctIndex ? '#34d399' : '#f87171' }}>
                            {selectedOpt === check.correctIndex ? t('¡Correcto!', 'Correct!') : t('Respuesta Incorrecta', 'Incorrect Answer')}
                          </div>
                          <div style={{ fontSize: '13px', color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                            {check.explanation}
                          </div>
                        </div>
                      )}

                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <button
                          className="btn btn-secondary"
                          onClick={() => handleNavigateLesson(activeView.moduleIndex, 0, 'lesson')}
                        >
                          <ChevronLeft size={16} /> {t('Volver al Módulo', 'Back to Module')}
                        </button>

                        {!submitted ? (
                          <button
                            className="btn btn-primary"
                            disabled={selectedOpt === undefined}
                            onClick={() => {
                              setInModuleSubmitted((prev) => ({ ...prev, [check.qId]: true }));
                              handleMarkModuleComplete(mod.moduleId);
                            }}
                          >
                            {t('Validar Respuesta', 'Submit Answer')}
                          </button>
                        ) : (
                          <button
                            className="btn btn-success"
                            onClick={() => {
                              if (activeView.moduleIndex < course.modules.length - 1) {
                                handleNavigateLesson(activeView.moduleIndex + 1, 0, 'lesson');
                              } else if (course.miniGame) {
                                setActiveView({ type: 'minigame' });
                              } else {
                                setActiveView({ type: 'exam' });
                              }
                            }}
                          >
                            {t('Siguiente Sección', 'Next Section')} <ChevronRight size={16} />
                          </button>
                        )}
                      </div>
                    </div>
                  );
                }

                // Regular Micro-Lesson View with Bilingual SVG Infographics
                const imageSrc = resolveAssetUrl(currentPage?.imageUrl, lang);

                return (
                  <div>
                    {/* Module Title Header */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '10px' }}>
                      <div>
                        <div style={{ fontSize: '12px', color: 'var(--accent-primary)', fontWeight: 700, textTransform: 'uppercase' }}>
                          {t('Módulo', 'Module')} {activeView.moduleIndex + 1} / {course.modules.length}
                        </div>
                        <h2 style={{ fontSize: '22px', fontWeight: 800, color: 'var(--text-primary)' }}>
                          {mod.title[lang]}
                        </h2>
                      </div>

                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        {mod.pages?.map((_, idx) => (
                          <button
                            key={idx}
                            onClick={() => handleNavigateLesson(activeView.moduleIndex, idx, 'lesson')}
                            style={{
                              width: '32px',
                              height: '32px',
                              borderRadius: '6px',
                              border: 'none',
                              background: activeView.pageIndex === idx ? 'var(--accent-primary)' : 'var(--bg-secondary)',
                              color: activeView.pageIndex === idx ? '#ffffff' : 'var(--text-secondary)',
                              fontWeight: 700,
                              fontSize: '12px',
                              cursor: 'pointer',
                            }}
                          >
                            {idx + 1}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Lesson Content Card */}
                    {currentPage && (
                      <div className="glass-card" style={{ padding: '24px', marginBottom: '24px' }}>
                        <h3 style={{ fontSize: '17px', fontWeight: 700, marginBottom: '14px', color: 'var(--text-primary)' }}>
                          {currentPage.title[lang]}
                        </h3>

                        {/* Modern Minimalist Bilingual Vector Infographic */}
                        {imageSrc && (
                          <div style={{ marginBottom: '22px', borderRadius: 'var(--radius-md)', overflow: 'hidden', border: '1px solid var(--border-color)', background: '#090d16', display: 'flex', justifyContent: 'center' }}>
                            <img
                              src={imageSrc}
                              alt="Diagram"
                              style={{ width: '100%', maxHeight: '280px', objectFit: 'contain' }}
                            />
                          </div>
                        )}

                        <p style={{ fontSize: '14.5px', lineHeight: 1.7, color: 'var(--text-secondary)', marginBottom: '20px' }}>
                          {currentPage.content[lang]}
                        </p>

                        {/* Key Takeaways Box */}
                        {currentPage.keyTakeaways && (
                          <div style={{ padding: '16px', borderRadius: 'var(--radius-sm)', background: 'var(--bg-secondary)', borderLeft: '4px solid var(--accent-primary)' }}>
                            <div style={{ fontWeight: 700, fontSize: '12.5px', color: 'var(--accent-primary)', marginBottom: '8px', textTransform: 'uppercase' }}>
                              {t('Puntos Clave de Cumplimiento', 'Key Compliance Takeaways')}
                            </div>
                            <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '13px', color: 'var(--text-primary)', lineHeight: 1.6 }}>
                              {currentPage.keyTakeaways[lang].map((item, i) => (
                                <li key={i}>{item}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Navigation Footer */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <button
                        className="btn btn-secondary"
                        disabled={activeView.pageIndex === 0 && activeView.moduleIndex === 0}
                        onClick={() => {
                          if (activeView.pageIndex > 0) {
                            handleNavigateLesson(activeView.moduleIndex, activeView.pageIndex - 1, 'lesson');
                          } else if (activeView.moduleIndex > 0) {
                            const prevMod = course.modules[activeView.moduleIndex - 1];
                            handleNavigateLesson(activeView.moduleIndex - 1, (prevMod.pages?.length || 1) - 1, 'lesson');
                          }
                        }}
                      >
                        <ChevronLeft size={16} /> {t('Anterior', 'Previous')}
                      </button>

                      {activeView.pageIndex < (mod.pages?.length || 1) - 1 ? (
                        <button
                          className="btn btn-primary"
                          onClick={() => handleNavigateLesson(activeView.moduleIndex, activeView.pageIndex + 1, 'lesson')}
                        >
                          {t('Siguiente Lección', 'Next Lesson')} <ChevronRight size={16} />
                        </button>
                      ) : mod.moduleAssessment ? (
                        <button
                          className="btn btn-warning"
                          style={{ background: 'var(--accent-warning)', color: '#000000', fontWeight: 700 }}
                          onClick={() => handleNavigateLesson(activeView.moduleIndex, 0, 'check')}
                        >
                          {t('Ir al Knowledge Check del Módulo', 'Take Module Knowledge Check')} <ChevronRight size={16} />
                        </button>
                      ) : (
                        <button
                          className="btn btn-success"
                          onClick={() => {
                            handleMarkModuleComplete(mod.moduleId);
                            if (activeView.moduleIndex < course.modules.length - 1) {
                              handleNavigateLesson(activeView.moduleIndex + 1, 0, 'lesson');
                            } else if (course.miniGame) {
                              setActiveView({ type: 'minigame' });
                            } else {
                              setActiveView({ type: 'exam' });
                            }
                          }}
                        >
                          {t('Completar Módulo', 'Complete Module')} <Check size={16} />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })()}
            </div>
          )}

          {/* VIEW 3: DECISION MINI-GAME & BRANCHING CRISIS SIMULATOR */}
          {activeView.type === 'minigame' && (
            course.branchingScenario ? (
              <BranchingScenarioGame
                scenario={course.branchingScenario}
                onComplete={handleCompleteBranchingScenario}
              />
            ) : course.miniGame ? (
              <div>
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '4px 12px', borderRadius: '12px', background: 'rgba(16, 185, 129, 0.15)', color: '#34d399', fontSize: '12px', fontWeight: 700, marginBottom: '12px' }}>
                  <Gamepad2 size={16} />
                  {t('Simulador de Decisiones Situacionales', 'Situational Decision Simulator')}
                </div>
                <h2 style={{ fontSize: '24px', fontWeight: 800, marginBottom: '16px' }}>
                  {course.miniGame.title[lang]}
                </h2>

                <div className="glass-card" style={{ padding: '24px', marginBottom: '24px', borderLeft: '4px solid #10b981' }}>
                  <div style={{ fontSize: '12px', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: '8px' }}>
                    {t('Escenario Realista', 'Realistic Scenario')}
                  </div>
                  <p style={{ fontSize: '15px', color: 'var(--text-primary)', lineHeight: 1.6, margin: 0 }}>
                    {course.miniGame.scenario[lang]}
                  </p>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', marginBottom: '28px' }}>
                  {course.miniGame.options.map((opt) => {
                    const isSelected = selectedGameOption === opt.optionId;
                    let bg = 'var(--bg-secondary)';
                    let border = '1px solid var(--border-color)';

                    if (gameResult) {
                      if (opt.isCorrect) {
                        bg = 'rgba(16, 185, 129, 0.2)';
                        border = '1.5px solid #10b981';
                      } else if (isSelected && !opt.isCorrect) {
                        bg = 'rgba(239, 68, 68, 0.2)';
                        border = '1.5px solid #ef4444';
                      }
                    } else if (isSelected) {
                      bg = 'rgba(16, 185, 129, 0.12)';
                      border = '1.5px solid #10b981';
                    }

                    return (
                      <button
                        key={opt.optionId}
                        disabled={!!gameResult}
                        onClick={() => setSelectedGameOption(opt.optionId)}
                        style={{
                          padding: '18px',
                          borderRadius: 'var(--radius-md)',
                          background: bg,
                          border,
                          textAlign: 'left',
                          color: 'var(--text-primary)',
                          fontSize: '14px',
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: '14px',
                          cursor: gameResult ? 'default' : 'pointer',
                          transition: 'all 0.15s ease',
                        }}
                      >
                        <div
                          style={{
                            width: '24px',
                            height: '24px',
                            borderRadius: '50%',
                            background: isSelected ? '#10b981' : 'var(--bg-primary)',
                            color: isSelected ? '#ffffff' : 'var(--text-secondary)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '12px',
                            fontWeight: 700,
                            flexShrink: 0,
                            marginTop: '2px',
                          }}
                        >
                          {opt.isCorrect && gameResult ? <Check size={14} /> : <Target size={14} />}
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: 600, lineHeight: 1.4 }}>{opt.text[lang]}</div>
                          {gameResult && isSelected && (
                            <div style={{ marginTop: '10px', fontSize: '13px', color: opt.isCorrect ? '#34d399' : '#f87171', lineHeight: 1.5 }}>
                              {opt.feedback[lang]}
                            </div>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <button
                    className="btn btn-secondary"
                    onClick={() => handleNavigateLesson(course.modules.length - 1, 0, 'lesson')}
                  >
                    <ChevronLeft size={16} /> {t('Volver a Módulos', 'Back to Modules')}
                  </button>

                  {!gameResult ? (
                    <button
                      className="btn btn-success"
                      disabled={!selectedGameOption}
                      onClick={() => {
                        const selected = course.miniGame?.options.find((o) => o.optionId === selectedGameOption);
                        if (selected) handleCompleteMiniGame(selected);
                      }}
                    >
                      {t('Confirmar Decisión (+100 Pts / +15% Progreso)', 'Confirm Decision (+100 Pts / +15% Progress)')}
                    </button>
                  ) : (
                    <button
                      className="btn btn-primary"
                      onClick={() => setActiveView({ type: 'exam' })}
                    >
                      {t('Ir a la Evaluación Final', 'Proceed to Final Exam')} <ChevronRight size={16} />
                    </button>
                  )}
                </div>
              </div>
            ) : null
          )}

          {/* VIEW 4: 12-QUESTION RANDOMIZED FINAL ASSESSMENT */}
          {activeView.type === 'exam' && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
                <div>
                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '4px 10px', borderRadius: '12px', background: 'rgba(59, 130, 246, 0.15)', color: '#60a5fa', fontSize: '12px', fontWeight: 700, marginBottom: '6px' }}>
                    <FileCheck size={14} />
                    {t('Evaluación Oficial de 12 Preguntas Aleatorias', 'Official 12-Question Randomized Assessment')}
                  </div>
                  <h2 style={{ fontSize: '24px', fontWeight: 800 }}>
                    {course.title[lang]}
                  </h2>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
                    {t('Umbral Mínimo:', 'Passing Score:')} <strong style={{ color: 'var(--accent-primary)' }}>{course.passingScore}%</strong>
                  </span>
                  <button
                    className="btn btn-secondary"
                    onClick={handleRetakeQuiz}
                    title="Generate new 12 random questions"
                    style={{ padding: '6px 10px', fontSize: '12px' }}
                  >
                    <RotateCcw size={14} /> {t('Generar Nuevo Muestreo', 'Resample 12')}
                  </button>
                </div>
              </div>

              {/* Assessment Results Card if Evaluated */}
              {quizResult ? (
                <div className="glass-card" style={{ padding: '36px', textAlign: 'center', marginBottom: '32px', border: `2px solid ${quizResult.passed ? '#10b981' : '#ef4444'}` }}>
                  <div
                    style={{
                      width: '72px',
                      height: '72px',
                      borderRadius: '50%',
                      background: quizResult.passed ? 'rgba(16, 185, 129, 0.2)' : 'rgba(239, 68, 68, 0.2)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 auto 16px',
                    }}
                  >
                    {quizResult.passed ? <Award size={36} color="#10b981" /> : <AlertTriangle size={36} color="#ef4444" />}
                  </div>

                  <h3 style={{ fontSize: '24px', fontWeight: 800, color: quizResult.passed ? '#34d399' : '#f87171', marginBottom: '8px' }}>
                    {quizResult.passed ? t('¡Aprobado con Éxito! (Curso 100% Completado)', 'Passed Successfully! (Course 100% Completed)') : t('Evaluación No Aprobada', 'Assessment Not Passed')}
                  </h3>

                  <div style={{ fontSize: '36px', fontWeight: 900, color: 'var(--text-primary)', margin: '12px 0' }}>
                    {quizResult.score}%
                  </div>

                  <p style={{ maxWidth: '600px', margin: '0 auto 24px', color: 'var(--text-secondary)', fontSize: '14px', lineHeight: 1.6 }}>
                    {quizResult.message}
                  </p>

                  <div style={{ display: 'flex', justifyContent: 'center', gap: '14px' }}>
                    {quizResult.passed && quizResult.badgeId ? (
                      <button
                        className="btn btn-success"
                        style={{ padding: '12px 24px' }}
                        onClick={() => navigate(`/verify/${quizResult.badgeId}`)}
                      >
                        <Award size={16} /> {t('Verificar Insignia Open Badges 3.0', 'Verify Open Badges 3.0')}
                      </button>
                    ) : (
                      <button
                        className="btn btn-primary"
                        style={{ padding: '12px 24px' }}
                        onClick={handleRetakeQuiz}
                      >
                        <RotateCcw size={16} /> {t('Intentar de Nuevo (Nuevo Muestreo)', 'Retake Exam (New 12 Questions)')}
                      </button>
                    )}
                    <button className="btn btn-secondary" onClick={() => navigate('/profile')}>
                      {t('Ir a Mi Perfil', 'Go to My Profile')}
                    </button>
                  </div>
                </div>
              ) : (
                <div>
                  {/* Interactive 12-Button Jump Matrix */}
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '24px', padding: '12px', background: 'var(--bg-secondary)', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-color)' }}>
                    {sampledQuestions.map((q, idx) => {
                      const isAnswered = selectedAnswers[q.qId] !== undefined;
                      const isCurrent = currentQuizIndex === idx;

                      return (
                        <button
                          key={q.qId}
                          onClick={() => setCurrentQuizIndex(idx)}
                          style={{
                            width: '38px',
                            height: '38px',
                            borderRadius: '8px',
                            border: isCurrent ? '2px solid var(--accent-primary)' : '1px solid var(--border-color)',
                            background: isAnswered ? 'rgba(16, 185, 129, 0.2)' : 'var(--bg-primary)',
                            color: isAnswered ? '#34d399' : 'var(--text-secondary)',
                            fontWeight: 700,
                            fontSize: '13px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'all 0.15s ease',
                          }}
                        >
                          {idx + 1}
                        </button>
                      );
                    })}
                  </div>

                  {/* Single Question Card */}
                  {sampledQuestions[currentQuizIndex] && (() => {
                    const currentQ = sampledQuestions[currentQuizIndex];
                    const selectedIdx = selectedAnswers[currentQ.qId];

                    return (
                      <div className="glass-card" style={{ padding: '28px', marginBottom: '24px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                          <span style={{ fontSize: '12px', color: 'var(--accent-primary)', fontWeight: 700, textTransform: 'uppercase' }}>
                            {t('Pregunta', 'Question')} {currentQuizIndex + 1} {t('de', 'of')} {sampledQuestions.length}
                          </span>
                          <span style={{ fontSize: '11.5px', color: selectedIdx !== undefined ? '#10b981' : '#f59e0b', fontWeight: 600 }}>
                            {selectedIdx !== undefined ? t('✓ Respondida', '✓ Answered') : t('○ Pendiente', '○ Pending')}
                          </span>
                        </div>

                        <h3 style={{ fontSize: '17px', fontWeight: 700, lineHeight: 1.5, marginBottom: '24px', color: 'var(--text-primary)' }}>
                          {currentQ.questionText[lang]}
                        </h3>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                          {currentQ.options[lang].map((optText, optIdx) => {
                            const isSelected = selectedIdx === optIdx;

                            return (
                              <button
                                key={optIdx}
                                onClick={() => handleQuizAnswer(currentQ.qId, optIdx)}
                                style={{
                                  padding: '16px',
                                  borderRadius: 'var(--radius-md)',
                                  background: isSelected ? 'rgba(59, 130, 246, 0.18)' : 'var(--bg-secondary)',
                                  border: isSelected ? '1.5px solid var(--accent-primary)' : '1px solid var(--border-color)',
                                  textAlign: 'left',
                                  color: 'var(--text-primary)',
                                  fontSize: '13.5px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '12px',
                                  cursor: 'pointer',
                                  transition: 'all 0.15s ease',
                                }}
                              >
                                <div
                                  style={{
                                    width: '24px',
                                    height: '24px',
                                    borderRadius: '50%',
                                    background: isSelected ? 'var(--accent-primary)' : 'var(--bg-primary)',
                                    color: isSelected ? '#ffffff' : 'var(--text-secondary)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '12px',
                                    fontWeight: 700,
                                    flexShrink: 0,
                                  }}
                                >
                                  {String.fromCharCode(65 + optIdx)}
                                </div>
                                <span style={{ flex: 1 }}>{optText}</span>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })()}

                  {/* Pending Confirmation Warning Dialog */}
                  {showPendingConfirm && (
                    <div
                      style={{
                        padding: '20px',
                        borderRadius: 'var(--radius-md)',
                        background: 'rgba(245, 158, 11, 0.15)',
                        border: '1.5px solid #f59e0b',
                        marginBottom: '24px',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#f59e0b', fontWeight: 700, fontSize: '15px', marginBottom: '8px' }}>
                        <AlertTriangle size={18} />
                        {t('Preguntas Pendientes de Respuesta', 'Unanswered Questions Pending')}
                      </div>
                      <p style={{ fontSize: '13.5px', color: 'var(--text-secondary)', lineHeight: 1.5, marginBottom: '16px' }}>
                        {t(
                          `Tienes ${sampledQuestions.filter((q) => selectedAnswers[q.qId] === undefined).length} pregunta(s) sin responder de las 12 seleccionadas. Si confirmas la entrega ahora, las preguntas pendientes se calificarán automáticamente como incorrectas (0 puntos).`,
                          `You have ${sampledQuestions.filter((q) => selectedAnswers[q.qId] === undefined).length} unanswered question(s). Unanswered questions will receive 0 points.`
                        )}
                      </p>
                      <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                        <button
                          className="btn btn-secondary"
                          onClick={() => {
                            const firstUnanswered = sampledQuestions.findIndex((q) => selectedAnswers[q.qId] === undefined);
                            if (firstUnanswered !== -1) setCurrentQuizIndex(firstUnanswered);
                            setShowPendingConfirm(false);
                          }}
                        >
                          {t('Ir a responder preguntas pendientes', 'Jump to Unanswered Questions')}
                        </button>
                        <button
                          className="btn btn-warning"
                          style={{ background: 'var(--accent-warning)', color: '#000000', fontWeight: 700 }}
                          onClick={() => handleSubmitQuizFinal(true)}
                          disabled={isSubmittingQuiz}
                        >
                          {t('Confirmar y Calificar con pendientes', 'Submit Anyway with 0 on Pending')}
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Navigation & Submit Footer */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <button
                      className="btn btn-secondary"
                      disabled={currentQuizIndex === 0}
                      onClick={() => setCurrentQuizIndex((prev) => Math.max(0, prev - 1))}
                    >
                      <ChevronLeft size={16} /> {t('Anterior', 'Previous')}
                    </button>

                    <div style={{ display: 'flex', gap: '12px' }}>
                      {currentQuizIndex < sampledQuestions.length - 1 ? (
                        <button
                          className="btn btn-secondary"
                          onClick={() => setCurrentQuizIndex((prev) => Math.min(sampledQuestions.length - 1, prev + 1))}
                        >
                          {t('Siguiente', 'Next')} <ChevronRight size={16} />
                        </button>
                      ) : null}

                      <button
                        className="btn btn-primary"
                        onClick={() => handleSubmitQuizFinal(false)}
                        disabled={isSubmittingQuiz}
                        style={{ padding: '10px 20px', fontWeight: 700 }}
                      >
                        {isSubmittingQuiz ? t('Calificando...', 'Grading...') : t('Finalizar y Calificar (12 Preguntas)', 'Finalize & Grade (12 Questions)')}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </main>
      </div>

      {/* BANANA IA / SYNTHESIA VIDEO PROMPT INSPECTOR MODAL */}
      {showVideoPromptModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.75)',
            backdropFilter: 'blur(6px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '24px',
          }}
          onClick={() => setShowVideoPromptModal(false)}
        >
          <div
            className="glass-card"
            style={{
              maxWidth: '680px',
              width: '100%',
              padding: '28px',
              borderRadius: 'var(--radius-lg)',
              border: '1px solid rgba(245, 158, 11, 0.3)',
              background: '#0b1120',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <Video size={22} color="#f59e0b" />
                <h3 style={{ fontSize: '18px', fontWeight: 800, margin: 0, color: 'var(--text-primary)' }}>
                  {t('Comandos y Prompts de Video IA (Banana IA / Synthesia)', 'Banana AI / Synthesia Video Prompts')}
                </h3>
              </div>
              <button
                className="btn btn-secondary"
                style={{ padding: '4px 8px', borderRadius: '50%' }}
                onClick={() => setShowVideoPromptModal(false)}
              >
                <X size={16} />
              </button>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <div style={{ fontSize: '11px', fontWeight: 700, color: '#f59e0b', textTransform: 'uppercase', marginBottom: '4px' }}>
                {t('Guión de Locución / Voiceover Narrative', 'Voiceover Script Narrative')} ({lang.toUpperCase()})
              </div>
              <p style={{ fontSize: '13.5px', color: 'var(--text-primary)', lineHeight: 1.6, background: 'var(--bg-secondary)', padding: '14px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border-color)' }}>
                "{lang === 'es' ? currentVideoPrompt.voiceover_es : currentVideoPrompt.voiceover_en}"
              </p>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <div style={{ fontSize: '11px', fontWeight: 700, color: '#38bdf8', textTransform: 'uppercase', marginBottom: '4px' }}>
                {t('Prompt Visual de Escena (Midjourney / Runway / Synthesia)', 'Visual Scene Prompt')}
              </div>
              <p style={{ fontSize: '12.5px', color: '#cbd5e1', lineHeight: 1.5, background: 'var(--bg-secondary)', padding: '12px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border-color)', fontFamily: 'monospace' }}>
                {currentVideoPrompt.visual_prompt}
              </p>
            </div>

            <div style={{ marginBottom: '24px' }}>
              <div style={{ fontSize: '11px', fontWeight: 700, color: '#34d399', textTransform: 'uppercase', marginBottom: '4px' }}>
                {t('Comando CLI de Generación Automática (Banana IA)', 'CLI Generation Command')}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', background: '#030712', padding: '12px 16px', borderRadius: 'var(--radius-sm)', border: '1px solid #1f2937', color: '#34d399', fontSize: '12px', fontFamily: 'monospace', gap: '10px' }}>
                <Terminal size={15} style={{ flexShrink: 0 }} />
                <code style={{ flex: 1, overflowX: 'auto', whiteSpace: 'nowrap' }}>
                  {currentVideoPrompt.api_command}
                </code>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
              <button
                className="btn btn-primary"
                onClick={() => {
                  navigator.clipboard.writeText(
                    `API Command: ${currentVideoPrompt.api_command}\n\nVoiceover: ${lang === 'es' ? currentVideoPrompt.voiceover_es : currentVideoPrompt.voiceover_en}\n\nVisual Prompt: ${currentVideoPrompt.visual_prompt}`
                  );
                  setCopiedPrompt(true);
                  setTimeout(() => setCopiedPrompt(false), 2000);
                }}
              >
                {copiedPrompt ? <Check size={16} /> : <Copy size={16} />}
                {copiedPrompt ? t('¡Copiado!', 'Copied!') : t('Copiar Prompts & Comandos', 'Copy Prompts & Commands')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
